/**
 * History API client
 * Requirements: 5.1, 5.2, 5.3, 5.4 - User history and analysis results
 */
import { createAuthenticatedClient } from '../../../shared/api/axiosClient';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

let apiClient: ReturnType<typeof createAuthenticatedClient> | null = null;

const getApiClient = () => {
  if (!apiClient) {
    apiClient = createAuthenticatedClient(API_BASE_URL, () => {
      window.location.href = '/login';
    });
  }
  return apiClient;
};

export interface HistoryItem {
  sessionId: string;
  questionSetId: string;
  questionSetTitle: string;
  completedAt: string;
  analysisResult: {
    id: string;
    scores: Record<string, number>;
    patternName: string | null;
    patternDescription: string | null;
  } | null;
}

export interface HistoryPage {
  items: HistoryItem[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface SessionDetail {
  sessionId: string;
  userId: string;
  userName: string;
  questionSetId: string;
  questionSetTitle: string;
  startedAt: string;
  completedAt: string | null;
  answers: Array<{
    questionId: string;
    questionText: string;
    questionOrder: number;
    answerChoiceId: string;
    answerChoiceText: string;
    thinkingTypeCode: string;
    answeredAt: string;
  }>;
  analysisResult: {
    id: string;
    scores: Record<string, number>;
    patternId: string | null;
    patternName: string | null;
    patternDescription: string | null;
    createdAt: string;
  } | null;
}

// Backend response format (different from frontend HistoryPage)
interface BackendHistoryResponse {
  items: HistoryItem[];
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
}

export const historyApi = {
  /**
   * Get paginated history for current user
   */
  async getHistory(page = 1, pageSize = 20): Promise<HistoryPage> {
    try {
      const client = getApiClient();
      const response = await client.get<BackendHistoryResponse>('/history', {
        params: { page, pageSize },
      });
      // Handle empty or malformed response
      if (!response.data || !response.data.items) {
        return {
          items: [],
          total: 0,
          page: 1,
          pageSize: 20,
          totalPages: 0,
        };
      }
      // Map backend response format to frontend HistoryPage format
      return {
        items: response.data.items,
        total: response.data.pagination?.totalItems ?? 0,
        page: response.data.pagination?.page ?? page,
        pageSize: response.data.pagination?.pageSize ?? pageSize,
        totalPages: response.data.pagination?.totalPages ?? 0,
      };
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get session detail
   */
  async getSessionDetail(sessionId: string): Promise<SessionDetail> {
    const client = getApiClient();
    const response = await client.get<SessionDetail>(`/history/sessions/${sessionId}`);
    return response.data;
  },

  /**
   * Delete history session
   */
  async deleteSession(sessionId: string): Promise<void> {
    const client = getApiClient();
    await client.delete(`/history/sessions/${sessionId}`);
  },
};
